import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import AppCloseIcon from "components/icons/app-close-icon";
import { formatDate, getDaysDifference } from "common/calendar";

const CARD_WIDTH = 300;
const CARD_OFFSET = 8;
const VIEWPORT_PADDING = 12;
const URGENT_DAYS_THRESHOLD = 7;

export const sortGrantsByExpiry = (grants) => {
	if (!Array.isArray(grants)) return [];

	return grants
		.filter((o) => !!o)
		.slice()
		.sort((a, b) => {
			const timeA = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
			const timeB = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;

			return timeA - timeB;
		});
};

const formatGrantDays = (days) => {
	const value = Number(days);

	if (!isFinite(value)) return "-";

	return `${value} ${value === 1 ? "day" : "days"}`;
};

const getDaysLeftChip = (expiryDate) => {
	if (!expiryDate) return null;

	const daysLeft = getDaysDifference(new Date(), expiryDate);

	if (daysLeft === null) return null;
	if (daysLeft < 0) return { label: "Expired", tone: "expired" };
	if (daysLeft === 0) return { label: "Today", tone: "urgent" };

	return { label: `${daysLeft} ${daysLeft === 1 ? "day left" : "days left"}`, tone: daysLeft <= URGENT_DAYS_THRESHOLD ? "urgent" : "default" };
};

const AppReplacementLeaveExpiry = (props) => {
	const pillRef = useRef();
	const cardRef = useRef();
	const [visible, setVisible] = useState(false);
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const grants = useMemo(() => sortGrantsByExpiry(props.grants), [props.grants]);
	const earliest = useMemo(() => grants.find((o) => !!o.expiryDate), [grants]);
	const remaining = useMemo(() => grants.length - 1, [grants.length]);

	const onHandlePosition = useCallback(() => {
		const pill = pillRef.current;

		if (!pill) return;

		const rect = pill.getBoundingClientRect();
		const cardHeight = cardRef.current?.offsetHeight || 0;
		const maxLeft = window.innerWidth - CARD_WIDTH - VIEWPORT_PADDING;
		const spaceBelow = window.innerHeight - rect.bottom;
		const openUpwards = cardHeight > 0 && spaceBelow < cardHeight + CARD_OFFSET + VIEWPORT_PADDING;

		setPosition({
			top: openUpwards ? rect.top - cardHeight - CARD_OFFSET : rect.bottom + CARD_OFFSET,
			left: Math.max(VIEWPORT_PADDING, Math.min(rect.right - CARD_WIDTH, maxLeft)),
		});
	}, []);

	const onHandleToggle = useCallback(() => {
		setVisible((prev) => !prev);
	}, []);

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
	}, []);

	useEffect(() => {
		if (!visible) return;

		onHandlePosition();

		const onHandleOutsideClick = (event) => {
			if (pillRef.current?.contains(event.target)) return;
			if (cardRef.current?.contains(event.target)) return;

			setVisible(false);
		};

		const onHandleKeyDown = (event) => {
			if (event.key === "Escape") setVisible(false);
		};

		document.addEventListener("mousedown", onHandleOutsideClick);
		document.addEventListener("keydown", onHandleKeyDown);
		window.addEventListener("resize", onHandlePosition);
		window.addEventListener("scroll", onHandlePosition, true);

		return () => {
			document.removeEventListener("mousedown", onHandleOutsideClick);
			document.removeEventListener("keydown", onHandleKeyDown);
			window.removeEventListener("resize", onHandlePosition);
			window.removeEventListener("scroll", onHandlePosition, true);
		};
	}, [visible, onHandlePosition]);

	if (!grants.length) return <div className="table__center--values">-</div>;

	return (
		<div className="app-replacement-leave-expiry">
			<p className="app-replacement-leave-expiry__date">{earliest ? formatDate(earliest.expiryDate) : "-"}</p>

			{remaining > 0 && (
				<button type="button" ref={pillRef} className="app-replacement-leave-expiry__pill" aria-expanded={visible} onClick={onHandleToggle}>
					{`+${remaining} more`}
				</button>
			)}

			{visible &&
				createPortal(
					<div className="app-replacement-leave-expiry__card" ref={cardRef} style={{ top: position.top, left: position.left, width: CARD_WIDTH }}>
						<div className="app-replacement-leave-expiry__header">
							<p className="app-replacement-leave-expiry__title">Replacement Leave Expiry</p>

							<button type="button" className="app-replacement-leave-expiry__close" aria-label="close" onClick={onHandleDismiss}>
								<AppCloseIcon color="#c0c0c0" />
							</button>
						</div>

						<ul className="app-replacement-leave-expiry__list">
							{grants.map((o, i) => {
								const chip = getDaysLeftChip(o.expiryDate);

								return (
									<li className="app-replacement-leave-expiry__item" key={o.id ?? i}>
										<div className="app-replacement-leave-expiry__column">
											<p className="app-replacement-leave-expiry__balance">{formatGrantDays(o.remainingDays)}</p>

											<p className="app-replacement-leave-expiry__caption">{o.expiryDate ? `Expires ${formatDate(o.expiryDate)}` : "-"}</p>
										</div>

										{!!chip && <span className={`app-replacement-leave-expiry__chip app-replacement-leave-expiry__chip--${chip.tone}`}>{chip.label}</span>}
									</li>
								);
							})}
						</ul>
					</div>,
					document.body
				)}
		</div>
	);
};

AppReplacementLeaveExpiry.propTypes = {
	grants: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			grantedDays: PropTypes.number,
			usedDays: PropTypes.number,
			remainingDays: PropTypes.number,
			expiryDate: PropTypes.string,
			status: PropTypes.string,
		})
	),
};

export default memo(AppReplacementLeaveExpiry);
